import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  ArrowLeft, 
  User, 
  Shield, 
  CreditCard, 
  Settings,
  CheckCircle,
  AlertTriangle,
  LogOut,
  Trash2,
  Landmark
} from 'lucide-react';
import { useAuth } from '../context/CompleteAuthContext';
import { toast } from 'sonner';
import ProfileTab from './settings/ProfileTab';
import SecurityTab from './settings/SecurityTab';
import BillingTab from './settings/BillingTab';

const AccountSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  // Get active tab from URL
  useEffect(() => {
    const tab = searchParams.get('tab') || 'profile';
    if (['profile', 'security', 'billing'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Redirect if not authenticated (but only after auth check completes)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ ...Object.fromEntries(searchParams), tab });
  };

  const handleBackToDashboard = () => {
    window.history.length > 1 ? navigate(-1) : navigate('/');
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading account settings...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
    { id: 'billing', label: 'Billing & Data' },
  ];

  const getVerificationStatus = () => {
    if (user.email_verified) {
      return <Badge className="bg-green-100 text-green-800 border-green-200">
        <CheckCircle className="w-3 h-3 mr-1" />
        Verified
      </Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
      <AlertTriangle className="w-3 h-3 mr-1" />
      Unverified
    </Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToDashboard}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-3 space-y-5">
                {/* ── ACCOUNT ─────────────────────────────── */}
                <SettingsSection title="Account">
                  <SettingsRow
                    icon={User}
                    active={activeTab === 'profile'}
                    onClick={() => handleTabChange('profile')}
                    label="Profile"
                    description="Manage your personal information"
                    testid="settings-row-profile"
                  />
                  <SettingsRow
                    icon={Shield}
                    active={activeTab === 'security'}
                    onClick={() => handleTabChange('security')}
                    label="Security"
                    description="Password & authentication"
                    testid="settings-row-security"
                  />
                </SettingsSection>

                {/* ── PAYMENTS ────────────────────────────── */}
                <SettingsSection title="Payments">
                  <SettingsRow
                    icon={Landmark}
                    onClick={() => navigate('/bank-accounts')}
                    label="Bank Accounts"
                    description="Receive payouts directly to your bank"
                    testid="settings-row-bank-accounts"
                  />
                  <SettingsRow
                    icon={CreditCard}
                    active={activeTab === 'billing'}
                    onClick={() => handleTabChange('billing')}
                    label="Billing & Data"
                    description="Billing, export, and account data"
                    testid="settings-row-billing"
                  />
                </SettingsSection>

                {/* ── ACCOUNT CONTROLS ───────────────────── */}
                <SettingsSection title="Account Controls">
                  <SettingsRow
                    icon={LogOut}
                    onClick={async () => { try { await logout(); } catch { /* noop */ } }}
                    label="Sign Out"
                    description="End your session on this device"
                    testid="settings-row-signout"
                  />
                  <SettingsRow
                    icon={Trash2}
                    danger
                    onClick={() => handleTabChange('billing')}
                    label="Delete Account"
                    description="Permanently remove your Payo account"
                    testid="settings-row-delete-account"
                  />
                </SettingsSection>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && <ProfileTab user={user} />}
            {activeTab === 'security' && <SecurityTab user={user} />}
            {activeTab === 'billing' && <BillingTab user={user} />}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center justify-center">
            <p className="text-sm text-gray-500">
              &copy; 2025 Payo Inc. • All Rights Reserved | Privacy Policy | Terms of Service
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AccountSettings;

/* ── Section + Row helpers ─────────────────────────────────── */

const SettingsSection = ({ title, children }) => (
  <div>
    <p className="px-3 mb-1 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">{title}</p>
    <nav className="space-y-0.5">{children}</nav>
  </div>
);

const SettingsRow = ({ icon: Icon, label, description, active, danger, onClick, testid }) => {
  const stateCls = active
    ? 'bg-blue-50 text-blue-700'
    : danger
      ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
      : 'text-gray-700 hover:bg-gray-50';
  const iconCls = active ? 'text-blue-600' : danger ? 'text-red-500' : 'text-gray-400';
  const labelCls = active ? 'text-blue-700' : danger ? 'text-red-600' : 'text-gray-900';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${stateCls}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconCls}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${labelCls}`}>{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </button>
  );
};